import { AxiosError } from 'axios';
import { apiService } from '@shared/services/apiServices';
import contactRequestEndpoints from './contactRequestEndpoints';
import {
  handleContactRequestError,
  ContactRequestErrorCode,
  contactRequestErrorMessages,
} from '../errors';
import type {
  ReassignContactRequestPayload,
  ReassignContactRequestResponseData,
  ReassignContactRequestResult,
} from '../types/assignContactRequestTypes';

/** Shown when a 409 indicates the request was reassigned (or moved on) first. */
export const ALREADY_REASSIGNED_ERROR =
  'This contact request has already been updated by another admin';

/**
 * Reassigns an already-`assigned` contact request to a different admin,
 * optionally emailing the new assignee.
 *
 * Endpoint: `POST contact-request/contact-requests/{id}/reassign`
 *
 * Body: `{ assignee_id, notify }` — `notify` opts into a best-effort email to
 * the new assignee (defaults to false). A 409 means another admin changed the
 * request first — surfaced as `conflict: true` so the caller can show
 * {@link ALREADY_REASSIGNED_ERROR} and refresh.
 *
 * Access: SUPER_ADMIN | ADMIN
 */
export const reassignContactRequest = async ({
  contactRequestId,
  assigneeId,
  notify,
}: ReassignContactRequestPayload): Promise<ReassignContactRequestResult> => {
  const trimmedId = contactRequestId.trim();
  if (!trimmedId || !assigneeId) {
    return {
      success: false,
      error: contactRequestErrorMessages[ContactRequestErrorCode.UNKNOWN],
    };
  }

  try {
    const result = await apiService.postAPI(
      contactRequestEndpoints.REASSIGN_CONTACT_REQUEST(trimmedId),
      { assignee_id: assigneeId, notify },
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        assignment?: ReassignContactRequestResponseData;
        notification_sent?: boolean;
      };
      const assignment = payload.assignment;
      // `notification_sent` may arrive nested in the assignment block or at the
      // top level of the response — accept either.
      const notificationSent =
        assignment?.notification_sent ?? payload.notification_sent;
      return {
        success: true,
        ...(assignment
          ? {
              data: {
                ...assignment,
                ...(notificationSent !== undefined
                  ? { notification_sent: notificationSent }
                  : {}),
              },
            }
          : {}),
      };
    }

    if (result.status === 409) {
      return {
        success: false,
        error: ALREADY_REASSIGNED_ERROR,
        conflict: true,
      };
    }

    const { errorMessage } = handleContactRequestError(
      result.status as number | undefined,
      result.data,
      !!result.isNetworkError
    );
    return { success: false, error: errorMessage };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 409) {
        return {
          success: false,
          error: ALREADY_REASSIGNED_ERROR,
          conflict: true,
        };
      }
      const { errorMessage } = handleContactRequestError(
        error.response?.status,
        error.response?.data,
        !error.response && !!error.request
      );
      return { success: false, error: errorMessage };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : contactRequestErrorMessages[
              ContactRequestErrorCode.UNEXPECTED_ERROR
            ],
    };
  }
};
